import {
  SearchFilterTypeEnum,
  SearchFilterTypeEnumLowercase,
} from "@components/atoms/Filters/utils";
import { StoneType } from "@scenes/bulk-request/Utils";
import { ProductCategory, SectionName } from "@utils/constants";
import { SearchResultsAndFavoritesEnum } from "./searchResultConstants";

const SKIPPED_ADDITIONAL_FILTER_KEYS = new Set([
  "price_total_from",
  "price_total_to",
]);

const isSelectedAdditionalFilterValue = (value: unknown) => {
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const additionalFilterPayloadNames = (filter: any) => {
  if (Array.isArray(filter?.payload_name)) return filter.payload_name;
  if (filter?.payload_name) return [filter.payload_name];
  return [];
};

const getProcessedQueryParams = ({
  productType,
  sortingFiltersParams,
  selectedGroups,
  payload,
  sectionName,
  jewelrySubType,
  filters,
  collection,
  sub_collection,
  enterSecondFlow,
  enableJewelryBuilder,
}: any) => {
  const applySecondFlow = enterSecondFlow && enableJewelryBuilder;
  let sortingFilterParamsObject = Object.assign(
    {},
    sortingFiltersParams ? { preference: sortingFiltersParams } : {}
  );

  if (
    sortingFilterParamsObject.preference.includes("discount_percent DESC") ||
    sortingFilterParamsObject.preference.includes("discount_percent ASC")
  ) {
    sortingFilterParamsObject.preference =
      sortingFilterParamsObject.preference.map((xOption: string) =>
        xOption === "discount_percent DESC"
          ? "discount_percent ASC"
          : xOption === "discount_percent ASC"
          ? "discount_percent DESC"
          : xOption
      );
  }

  let mGroupIds = selectedGroups[productType];
  let vdbSetting = false;
  if (mGroupIds) {
    if (mGroupIds.includes(0)) {
      vdbSetting = true;
      mGroupIds = mGroupIds.filter((groupId: number) => groupId !== 0);
    }
  }
  let queryParamList = {};
  switch (productType) {
    case ProductCategory.DIAMOND:
    case ProductCategory.LAB_GROWN_DIAMOND:
    case ProductCategory.GEMSTONE:
      stoneQueryParams(
        sectionName,
        filters,
        payload,
        queryParamList,
        sortingFilterParamsObject
      );
      break;
    case ProductCategory.JEWELRY:
    case ProductCategory.LABGROWN_JEWELRY:
      jewelryQueryParams(
        payload,
        sectionName,
        jewelrySubType,
        queryParamList,
        sortingFilterParamsObject
      );
      break;
    default:
      break;
  }

  const defaultPayload = (Array.isArray(filters) ? filters : []).find(
    ({ name }: { name: string }) =>
      name?.toLowerCase() === sectionName?.toLowerCase()
  )?.default_payload;

  Object.assign(queryParamList, defaultPayload ?? {});
  if (vdbSetting) {
    queryParamList = Object.assign(queryParamList, {
      vdb_setting: `${vdbSetting}`,
    });
  }

  if (mGroupIds && mGroupIds.length > 0) {
    queryParamList = Object.assign(queryParamList, {
      group_ids: mGroupIds,
    });
  }

  let cpsModifiedKey = "";
  if ("cps" in queryParamList) {
    if ("eight_x" in queryParamList) {
      cpsModifiedKey = "cps_eight_x_options";
    }
    if ("three_x" in queryParamList) {
      cpsModifiedKey = "cps_three_x_options";
    }
    if ("three_vg_plus" in queryParamList) {
      cpsModifiedKey = "cps_three_vg_plus_options";
    }

    const { cps, ...rest } = queryParamList;
    const newQueryParamList = {
      [cpsModifiedKey]: cps,
      ...rest,
    };

    queryParamList = newQueryParamList;
  }
  if (SearchResultsAndFavoritesEnum.HEARTS_AND_ARROWS in queryParamList) {
    if (
      Array.isArray(queryParamList.hearts_and_arrows) &&
      queryParamList.hearts_and_arrows.length === 1
    ) {
      queryParamList.hearts_and_arrows = queryParamList.hearts_and_arrows[0];
    }
  }

  if (SearchResultsAndFavoritesEnum.TREATMENT in queryParamList) {
    if (
      Array.isArray(queryParamList.treatment) &&
      queryParamList.treatment.length === 1
    ) {
      queryParamList.treatment = queryParamList.treatment[0];
    }
  }

  if (!applySecondFlow) {
    if (
      sub_collection &&
      Array.isArray(sub_collection) &&
      sub_collection?.length > 0 &&
      !("cert_num" in queryParamList)
    ) {
      // A subcategory such as diamond-fashion-rings is the collection to
      // search. Parent names stay in redux for the menu and are not sent.
      queryParamList.collection_names = sub_collection;
      delete queryParamList.sub_collection;
    } else if (
      collection &&
      Array.isArray(collection) &&
      collection?.length > 0 &&
      !(sub_collection?.length > 0) &&
      !("cert_num" in queryParamList)
    ) {
      queryParamList.collection_names = collection;
    }
  }

  try {
    const isJewelrySearch =
      productType === ProductCategory.JEWELRY ||
      productType === ProductCategory.LABGROWN_JEWELRY;
    const sectionData =
      productType === ProductCategory.LABGROWN_JEWELRY ||
      (productType === ProductCategory.JEWELRY &&
        sectionName === SectionName.LABGROWN_JEWELRY)
        ? payload?.["Lab-Grown Jewelry"]
        : payload?.Jewelry;

    if (isJewelrySearch && sectionData && !applySecondFlow) {
      const nextAdditionalFilters: Record<string, any> = {};
      const sections = Array.isArray(filters) ? filters : [];

      sections.forEach((section: any) => {
        const sectionFilters = Array.isArray(section?.filters)
          ? section.filters
          : [];
        sectionFilters.forEach((filter: any) => {
          if (filter?.additional_filters !== true) return;
          additionalFilterPayloadNames(filter).forEach(
            (payloadName: string) => {
              if (
                !payloadName ||
                SKIPPED_ADDITIONAL_FILTER_KEYS.has(payloadName)
              ) {
                return;
              }
              const matchingData = sectionData?.[payloadName];
              if (isSelectedAdditionalFilterValue(matchingData)) {
                nextAdditionalFilters[payloadName] = matchingData;
              }
            }
          );
        });
      });

      // A navigation link stores the previous request's additional_filters.
      // Send those keys again only when the same value is still selected, so
      // a field such as config_field_6 survives a reload and a cleared field
      // such as config_field_15 does not.
      const storedAdditionalFilters = sectionData.additional_filters;
      if (
        storedAdditionalFilters &&
        typeof storedAdditionalFilters === "object" &&
        !Array.isArray(storedAdditionalFilters)
      ) {
        Object.keys(storedAdditionalFilters).forEach((key) => {
          if (SKIPPED_ADDITIONAL_FILTER_KEYS.has(key)) return;
          if (key in nextAdditionalFilters) return;
          const currentValue = sectionData[key];
          if (isSelectedAdditionalFilterValue(currentValue)) {
            nextAdditionalFilters[key] = currentValue;
          }
        });
      }

      if (Object.keys(nextAdditionalFilters).length > 0) {
        queryParamList.additional_filters = nextAdditionalFilters;
      } else {
        delete queryParamList.additional_filters;
      }
    }
  } catch (error) {
    console.error("Error processing additional filters:", error);
  }

  const sectionPayload =
    payload?.[sectionName] || payload?.[SearchFilterTypeEnum.SINGLE_STONES];
  if (sectionPayload?._exclude && typeof sectionPayload._exclude === "object") {
    const exclude: Record<string, string[]> = {};
    Object.keys(sectionPayload._exclude).forEach((key) => {
      if (
        Array.isArray(sectionPayload._exclude[key]) &&
        sectionPayload._exclude[key].length > 0
      ) {
        exclude[key] = sectionPayload._exclude[key];
      }
    });
    if (Object.keys(exclude).length > 0) {
      queryParamList.exclude = exclude;
    }
  }

  if (queryParamList._exclude) {
    delete queryParamList._exclude;
  }

  return queryParamList;
};

const getPayloadName = (filters, tabName) => {
  const matched = filters?.find((item) => item?.name === tabName);
  return matched?.payload_name ? matched?.payload_name : null;
};

const convertJewelryPayloadSubOptions = (json: any, key: string) => {
  let newjson: any = {};
  if (json[key] && Array.isArray(json[key]) && json[key].length) {
    if (
      json[key][0] !== null &&
      typeof json[key][0] === "object" &&
      !Array.isArray(json[key][0])
    ) {
      json[key].forEach((item: object) => {
        Object.assign(newjson, item);
      });
      return key === "metals" ? [newjson] : newjson;
    }

    json[key].forEach((style: string) => {
      newjson[style] = [];
      if (json[style] && Array.isArray(json[style]) && json[style].length) {
        json[style].forEach((subtype: string) => {
          newjson[style].push(subtype);
        });
      } else newjson[style].push("");
    });
    if (key === "metals") {
      return [newjson];
    }
    return newjson;
  }
};

const stoneQueryParams = (
  sectionName: any,
  filters: any,
  payload: any,
  queryParamList: {},
  sortingFilterParamsObject: { preference: any } | { preference?: undefined }
) => {
  if (sectionName === SearchFilterTypeEnum.PARCEL) {
    const parcelPayloadName = getPayloadName(filters, "Parcel")?.[0];

    if (Object.keys(payload || {}).length === 0) {
      payload.Parcel = { pair: parcelPayloadName?.pair };
    }
    queryParamList = Object.assign(queryParamList, {
      ...payload.Parcel,
      ...sortingFilterParamsObject,
      ...(parcelPayloadName?.pair && { pair: parcelPayloadName?.pair }),
    });
  } else if (
    payload.Pairs?.pair === StoneType.PAIR &&
    payload.Pairs?.pair === SearchFilterTypeEnumLowercase.PAIRS &&
    sectionName === SearchFilterTypeEnum.PAIRS
  ) {
    queryParamList = Object.assign(queryParamList, {
      ...payload.Pairs,
      ...sortingFilterParamsObject,
      pair:
        getPayloadName(filters, "Pairs")?.[0] ||
        SearchFilterTypeEnumLowercase.PAIRS,
    });
  } else if (payload && payload[SearchFilterTypeEnum.SINGLE_STONES]) {
    queryParamList = Object.assign(queryParamList, {
      ...payload[SearchFilterTypeEnum.SINGLE_STONES],
      ...sortingFilterParamsObject,
      ...(getPayloadName(filters, "Single Stones")?.[0] && {
        pair: getPayloadName(filters, "Single Stones")?.[0],
      }),
    });
  } else {
    queryParamList = Object.assign(queryParamList, {
      ...sortingFilterParamsObject,
      ...(getPayloadName(filters, sectionName)?.[0] && {
        pair: getPayloadName(filters, sectionName)?.[0],
      }),
    });
  }
  return queryParamList;
};

const resolveJewelrySectionName = (payload: any, sectionName: any) => {
  if (
    sectionName === SearchFilterTypeEnum.JEWELRY ||
    sectionName === SearchFilterTypeEnum.LAB_GROWN_JEWELRY
  ) {
    return sectionName;
  }
  // A stale stone tab name (Single Stones) must not replace the setting
  // search with the leftover default stone payload.
  if (payload?.[SearchFilterTypeEnum.JEWELRY]) {
    return SearchFilterTypeEnum.JEWELRY;
  }
  if (payload?.[SearchFilterTypeEnum.LAB_GROWN_JEWELRY]) {
    return SearchFilterTypeEnum.LAB_GROWN_JEWELRY;
  }
  return sectionName;
};

const jewelryQueryParams = (
  payload: any,
  sectionName: any,
  jewelrySubType: any,
  queryParamList: {},
  sortingFilterParamsObject: { preference: any } | { preference?: undefined }
) => {
  const jewelrySectionName = resolveJewelrySectionName(payload, sectionName);
  if (
    payload &&
    (payload[jewelrySectionName] || payload[SearchFilterTypeEnum.JEWELRY])
  ) {
    let oldPayload = {};

    try {
      if (payload[jewelrySectionName ?? SearchFilterTypeEnum.JEWELRY]) {
        oldPayload = JSON.parse(
          JSON.stringify(
            payload[jewelrySectionName ?? SearchFilterTypeEnum.JEWELRY]
          )
        );
      }
    } catch (error) {
      console.error("Error parsing payload:", error);

      oldPayload =
        payload[
          jewelrySectionName ? jewelrySectionName : SearchFilterTypeEnum.JEWELRY
        ] || {};
    }
    let jewelryPayload: any = {};
    if (oldPayload?.jewelry_styles) {
      jewelryPayload.jewelry_styles = convertJewelryPayloadSubOptions(
        oldPayload,
        "jewelry_styles"
      );
    }
    if (oldPayload?.metals)
      jewelryPayload.metals = convertJewelryPayloadSubOptions(
        oldPayload,
        "metals"
      );
    if (oldPayload?.price_total_from)
      jewelryPayload.price_total_from = oldPayload.price_total_from;
    if (oldPayload?.price_total_to)
      jewelryPayload.price_total_to = oldPayload.price_total_to;
    if (oldPayload?.size_from) jewelryPayload.size_from = oldPayload.size_from;
    if (oldPayload?.size_to) jewelryPayload.size_to = oldPayload.size_to;
    if (oldPayload?.vendor_locations)
      jewelryPayload.vendor_locations = oldPayload.vendor_locations;
    if (oldPayload?.company_names)
      jewelryPayload.company_names = oldPayload.company_names;
    if (oldPayload?.brands) {
      jewelryPayload.brands = oldPayload.brands;
    }
    if (oldPayload?.stock_item_ids) {
      jewelryPayload.stock_item_ids = oldPayload.stock_item_ids;
    }
    if (oldPayload?.cert_num) {
      jewelryPayload.cert_num = oldPayload?.cert_num;
    }
    if (jewelrySubType) {
      jewelryPayload.sub_type = jewelrySubType;
    }
    Object.keys(oldPayload).forEach((key) => {
      if (!(key in jewelryPayload)) {
        jewelryPayload[key] = oldPayload[key];
      }
    });
    // Rebuilt later from the filters that are still selected. A stored copy
    // keeps attributes from the previous navigation link (for example
    // config_field_15 after switching away from Five Stone).
    delete jewelryPayload.additional_filters;
    queryParamList = Object.assign(queryParamList, {
      ...jewelryPayload,
      ...sortingFilterParamsObject,
    });
  } else if (payload?.fromSavedSearch) {
    delete payload.fromSavedSearch;
    queryParamList = Object.assign(queryParamList, {
      ...payload,
    });
  } else if (jewelrySubType) {
    let jewelryPayload = {};
    jewelryPayload.sub_type = jewelrySubType;

    queryParamList = Object.assign(queryParamList, {
      ...jewelryPayload,
      ...sortingFilterParamsObject,
    });
  } else if (sortingFilterParamsObject) {
    let jewelryPayload = {};
    queryParamList = Object.assign(queryParamList, {
      ...jewelryPayload,
      ...sortingFilterParamsObject,
    });
  }
  return queryParamList;
};

export { getProcessedQueryParams };
